/**
 * Format Selector
 *
 * Select social media formats (Instagram, Facebook, etc.)
 */

import { Instagram, Facebook, Linkedin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Checkbox } from '@/components/v2/ui';
import type { SocialFormat } from '@/lib/api/content-studio';

interface FormatSelectorProps {
  formats: SocialFormat[];
  selectedFormats: string[];
  onToggle: (formatId: string) => void;
}

const PLATFORM_ICONS: Record<string, React.ReactNode> = {
  instagram: <Instagram className="h-4 w-4" />,
  facebook: <Facebook className="h-4 w-4" />,
  linkedin: <Linkedin className="h-4 w-4" />,
  pinterest: <span className="h-4 w-4 font-bold text-xs">P</span>,
};

export function FormatSelector({
  formats,
  selectedFormats,
  onToggle,
}: FormatSelectorProps) {
  // Group formats by platform
  const groupedFormats = formats.reduce(
    (acc, format) => {
      const platform = format.platform;
      if (!acc[platform]) {
        acc[platform] = [];
      }
      acc[platform].push(format);
      return acc;
    },
    {} as Record<string, SocialFormat[]>
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Formate auswählen</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {Object.entries(groupedFormats).map(([platform, platformFormats]) => (
            <div key={platform}>
              <div className="flex items-center gap-2 mb-3 text-sm font-medium text-neutral-600 dark:text-neutral-400">
                {PLATFORM_ICONS[platform]}
                <span className="capitalize">{platform}</span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {platformFormats.map((format) => {
                  const isSelected = selectedFormats.includes(format.id);

                  return (
                    <label
                      key={format.id}
                      className={`
                        flex items-start gap-3 p-3 rounded-lg border cursor-pointer
                        transition-all duration-200
                        ${
                          isSelected
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300'
                        }
                      `}
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={() => onToggle(format.id)}
                      />
                      <div className="flex-1">
                        <div className="font-medium text-sm">{format.name}</div>
                        <div className="text-xs text-neutral-500">
                          {format.width} x {format.height} ({format.ratio})
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {selectedFormats.length === 0 && (
          <p className="text-sm text-amber-600 mt-4">
            Bitte mindestens ein Format auswählen
          </p>
        )}
      </CardContent>
    </Card>
  );
}
